import { GlButton, GlCard, GlFormTextarea, GlFormGroup, GlBadge, GlDropdown, GlDropdownItem } from '@gitlab/ui';
import { translate } from '../../../../utils/i18n';
import { convertKeysToCamelCase } from '../../../../utils/object';
import { APPROVAL_TOOL_NAMES, acceptedApproveToolPayloads } from '../../constants';
import CreateCommitToolParams from './components/create_commit_tool_params';
import IssuableToolParams from './components/issuable_tool_params';
import RunCommandToolParams from './components/run_command_tool_params';
import PreBlock from './components/pre_block';
import __vue_normalize__ from 'vue-runtime-helpers/dist/normalize-component.js';

const PROCESSING_STATE = {
  APPROVING: 'approving',
  DENYING: 'denying',
  NONE: null
};
const TOOL_STATUS = {
  Pending: 'pending',
  Approved: 'approved'
};
const i18n = {
  TOOL_APPROVAL_DESCRIPTION: translate('MessageToolApproval.toolApprovalDescription', 'GitLab Duo Agentic Chat wants to execute a tool. Do you want to proceed?'),
  TOOL_LABEL: translate('MessageToolApproval.toolLabel', 'Tool:'),
  TOOL_UNKNOWN: translate('MessageToolApproval.toolUnknown', 'Unknown'),
  APPROVE_TEXT: translate('MessageToolApproval.approveText', 'Approve'),
  APPROVE_SESSION_TEXT: translate('MessageToolApproval.approveSessionText', 'Approve for session'),
  APPROVE_ALL_TEXT: translate('MessageToolApproval.approveAllText', 'Always approve this tool'),
  APPROVING_TEXT: translate('MessageToolApproval.approvingText', 'Approving...'),
  DENY_TEXT: translate('MessageToolApproval.denyText', 'Deny'),
  DENYING_TEXT: translate('MessageToolApproval.denyingText', 'Denying...'),
  NO_PARAMETERS_TEXT: translate('MessageToolApproval.noParametersText', 'No parameters will be sent with this request.'),
  REQUEST_TEXT: translate('MessageToolApproval.parametersText', 'Request parameters'),
  DENIAL_REASON_LABEL: translate('MessageToolApproval.denialReasonLabel', 'Rejection reason'),
  DENIAL_REASON_PLACEHOLDER: translate('MessageToolApproval.denialReasonPlaceholder', "Tell Duo why you're rejecting this tool execution..."),
  CANCEL_TEXT: translate('MessageToolApproval.cancelText', 'Cancel'),
  TOOL_APPROVAL_TITLES: {
    [APPROVAL_TOOL_NAMES.createCommit]: translate('MessageToolApproval.createCommit', 'Duo wants to push a commit.'),
    [APPROVAL_TOOL_NAMES.createIssue]: translate('MessageToolApproval.createIssue', 'Duo wants to open an issue.'),
    [APPROVAL_TOOL_NAMES.updateIssue]: translate('MessageToolApproval.updateIssue', 'Duo wants to update an issue.'),
    [APPROVAL_TOOL_NAMES.createEpic]: translate('MessageToolApproval.createEpic', 'Duo wants to create an epic.'),
    [APPROVAL_TOOL_NAMES.updateEpic]: translate('MessageToolApproval.updateEpic', 'Duo wants to update an epic.'),
    [APPROVAL_TOOL_NAMES.createWorkItem]: translate('MessageToolApproval.createWorkItem', 'Duo wants to create a work item.'),
    [APPROVAL_TOOL_NAMES.updateWorkItem]: translate('MessageToolApproval.updateWorkItem', 'Duo wants to update a work item.'),
    [APPROVAL_TOOL_NAMES.createMergeRequest]: translate('MessageToolApproval.createMergeRequest', 'Duo wants to create a merge request.'),
    [APPROVAL_TOOL_NAMES.createIssueNote]: translate('MessageToolApproval.createIssueNote', 'Duo wants to post a note on an issue.'),
    [APPROVAL_TOOL_NAMES.createMergeRequestNote]: translate('MessageToolApproval.createMergeRequestNote', 'Duo wants to post a note on a merge request.'),
    [APPROVAL_TOOL_NAMES.createWorkItemNote]: translate('MessageToolApproval.createWorkItemNote', 'Duo wants to post a note on a work item.'),
    [APPROVAL_TOOL_NAMES.runCommand]: translate('MessageToolApproval.runCommand', 'Duo wants to run a command.'),
    [APPROVAL_TOOL_NAMES.runGitCommand]: translate('MessageToolApproval.runGitCommand', 'Duo wants to run a git command.')
  },
  [TOOL_STATUS.Pending]: translate('MessageToolApproval.toolStatusPending', 'Pending'),
  [TOOL_STATUS.Approved]: translate('MessageToolApproval.toolStatusApproved', 'Approved'),
  TOGGLE_PARAMS_BUTTON_EXPAND: translate('MessageToolApproval.collapseButtonCollapsed', 'Display tool details'),
  TOGGLE_PARAMS_BUTTON_COLLAPSE: translate('MessageToolApproval.collapseButtonExpanded', 'Hide tool details'),
  MULTI_TOOL_TITLE: translate('MessageToolApproval.multiToolTitle', 'Duo wants to execute %{count} tools.')
};
const TOOL_PARAMS_VIEW_COMPONENTS = {
  [APPROVAL_TOOL_NAMES.createCommit]: 'CreateCommitToolParams',
  [APPROVAL_TOOL_NAMES.createIssue]: 'IssuableToolParams',
  [APPROVAL_TOOL_NAMES.updateIssue]: 'IssuableToolParams',
  [APPROVAL_TOOL_NAMES.createEpic]: 'IssuableToolParams',
  [APPROVAL_TOOL_NAMES.updateEpic]: 'IssuableToolParams',
  [APPROVAL_TOOL_NAMES.createMergeRequest]: 'IssuableToolParams',
  [APPROVAL_TOOL_NAMES.updateMergeRequest]: 'IssuableToolParams',
  [APPROVAL_TOOL_NAMES.createWorkItem]: 'IssuableToolParams',
  [APPROVAL_TOOL_NAMES.updateWorkItem]: 'IssuableToolParams',
  [APPROVAL_TOOL_NAMES.createMergeRequestNote]: 'IssuableToolParams',
  [APPROVAL_TOOL_NAMES.createWorkItemNote]: 'IssuableToolParams',
  [APPROVAL_TOOL_NAMES.createIssueNote]: 'IssuableToolParams',
  [APPROVAL_TOOL_NAMES.runCommand]: 'RunCommandToolParams',
  [APPROVAL_TOOL_NAMES.runGitCommand]: 'RunCommandToolParams'
};
var script = {
  name: 'MessageToolApproval',
  components: {
    GlButton,
    GlCard,
    GlFormTextarea,
    GlFormGroup,
    GlBadge,
    GlDropdown,
    GlDropdownItem,
    CreateCommitToolParams,
    IssuableToolParams,
    RunCommandToolParams,
    PreBlock
  },
  props: {
    messages: {
      required: true,
      type: Array
    },
    workingDirectory: {
      type: String,
      required: false,
      default: ''
    },
    isProcessing: {
      type: Boolean,
      required: false,
      default: false
    },
    approvalStatus: {
      type: String,
      required: false,
      default: TOOL_STATUS.Pending
    },
    /**
     * Array of approval options for multi-approval functionality
     * @property {string} type - The approval type (approve-tool-once, approve-for-session, always-approve-tool)
     * @property {string} text - The display text for the option
     * @property {boolean} primary - Whether this is the primary option (appears as main button)
     * @property {boolean} disabled - Whether this option is disabled
     */
    approvalOptions: {
      type: Array,
      required: false,
      default: () => [{
        type: acceptedApproveToolPayloads.APPROVE_TOOL_ONCE,
        text: i18n.APPROVE_TEXT,
        primary: true
      }],
      validator(value) {
        if (!Array.isArray(value)) return false;
        // Must have at least one approve-tool-once option
        return value.some(option => option.type === acceptedApproveToolPayloads.APPROVE_TOOL_ONCE);
      }
    }
  },
  data() {
    return {
      showDenialReason: false,
      denialReason: '',
      localProcessingState: PROCESSING_STATE.NONE,
      collapsed: this.approvalStatus === TOOL_STATUS.Approved
    };
  },
  computed: {
    multipleApprovalsNeeded() {
      return this.messages.length > 1;
    },
    primaryMessage() {
      return this.messages[0] || null;
    },
    toolName() {
      var _this$primaryMessage, _this$primaryMessage$;
      return ((_this$primaryMessage = this.primaryMessage) === null || _this$primaryMessage === void 0 ? void 0 : (_this$primaryMessage$ = _this$primaryMessage.tool_info) === null || _this$primaryMessage$ === void 0 ? void 0 : _this$primaryMessage$.name) || this.$options.i18n.TOOL_UNKNOWN;
    },
    toolParameters() {
      var _this$primaryMessage2, _this$primaryMessage3;
      return ((_this$primaryMessage2 = this.primaryMessage) === null || _this$primaryMessage2 === void 0 ? void 0 : (_this$primaryMessage3 = _this$primaryMessage2.tool_info) === null || _this$primaryMessage3 === void 0 ? void 0 : _this$primaryMessage3.args) || {};
    },
    toolResponse() {
      var _this$primaryMessage4, _this$primaryMessage5;
      return (_this$primaryMessage4 = this.primaryMessage) === null || _this$primaryMessage4 === void 0 ? void 0 : (_this$primaryMessage5 = _this$primaryMessage4.tool_info) === null || _this$primaryMessage5 === void 0 ? void 0 : _this$primaryMessage5.tool_response;
    },
    camelCaseToolParameters() {
      return convertKeysToCamelCase(this.toolParameters);
    },
    hasToolParameters() {
      return Object.keys(this.toolParameters).length > 0;
    },
    toolApprovalTitle() {
      // Multiple tools: show generic count message
      if (this.multipleApprovalsNeeded) {
        return this.$options.i18n.MULTI_TOOL_TITLE.replace('%{count}', this.messages.length);
      }

      // Single tool: show specific tool message
      return i18n.TOOL_APPROVAL_TITLES[this.toolName] || this.toolName;
    },
    toolStatusLabel() {
      return i18n[this.approvalStatus];
    },
    toolStatusVariant() {
      return {
        [TOOL_STATUS.Pending]: 'neutral',
        [TOOL_STATUS.Approved]: 'info'
      }[this.approvalStatus];
    },
    collapsible() {
      return this.approvalStatus === TOOL_STATUS.Approved;
    },
    collapseButtonProps() {
      return this.collapsed ? {
        icon: 'chevron-right',
        title: i18n.TOGGLE_PARAMS_BUTTON_EXPAND
      } : {
        icon: 'chevron-down',
        title: i18n.TOGGLE_PARAMS_BUTTON_COLLAPSE
      };
    },
    isApproved() {
      return this.approvalStatus === TOOL_STATUS.Approved;
    },
    isApproving() {
      return this.isProcessing && this.localProcessingState === PROCESSING_STATE.APPROVING;
    },
    isDenying() {
      return this.isProcessing && this.localProcessingState === PROCESSING_STATE.DENYING;
    },
    buttonsDisabled() {
      return this.isProcessing;
    },
    approveButtonText() {
      return this.isApproving ? this.$options.i18n.APPROVING_TEXT : this.$options.i18n.APPROVE_TEXT;
    },
    denyButtonText() {
      return this.isDenying ? this.$options.i18n.DENYING_TEXT : this.$options.i18n.DENY_TEXT;
    },
    toolParamsViewComponent() {
      return TOOL_PARAMS_VIEW_COMPONENTS[this.toolName];
    },
    safeApprovalOptions() {
      return this.approvalOptions || [];
    },
    primaryApprovalOption() {
      const primary = this.safeApprovalOptions.find(option => option.primary === true);
      return primary || this.safeApprovalOptions[0] || {};
    },
    additionalApprovalOptions() {
      return this.safeApprovalOptions.filter(option => option.primary !== true);
    },
    showSplitButton() {
      return this.safeApprovalOptions.length > 1;
    }
  },
  watch: {
    // Reset local state when processing completes
    isProcessing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.resetProcessingState();
      }
    }
  },
  methods: {
    handleApprove() {
      if (this.isProcessing) return;
      this.localProcessingState = PROCESSING_STATE.APPROVING;
      // Default to primary option type for backward compatibility
      const approvalPayload = {
        type: this.primaryApprovalOption.type || acceptedApproveToolPayloads.APPROVE_TOOL_ONCE
      };
      this.$emit('approve-tool', approvalPayload);
    },
    handlePrimaryApprove() {
      if (this.isProcessing) return;
      this.localProcessingState = PROCESSING_STATE.APPROVING;
      this.$emit('approve-tool', {
        type: this.primaryApprovalOption.type
      });
    },
    handleDropdownSelection(option) {
      if (this.isProcessing) return;
      this.localProcessingState = PROCESSING_STATE.APPROVING;
      this.$emit('approve-tool', {
        type: option.type
      });
    },
    handleDeny() {
      if (this.isProcessing) return;
      this.showDenialReason = true;
    },
    cancelDenial() {
      this.showDenialReason = false;
      this.denialReason = '';
      this.localProcessingState = PROCESSING_STATE.NONE;
    },
    submitDenial() {
      if (this.isProcessing) return;
      this.localProcessingState = PROCESSING_STATE.DENYING;
      this.$emit('deny-tool', this.denialReason || null);
      // Don't reset state here - wait for isProcessing to change
    },
    resetProcessingState() {
      this.localProcessingState = PROCESSING_STATE.NONE;
      this.showDenialReason = false;
      this.denialReason = '';
    },
    convertKeysToCamelCase(obj) {
      return convertKeysToCamelCase(obj);
    },
    getToolParamsComponent(toolName) {
      return TOOL_PARAMS_VIEW_COMPONENTS[toolName];
    }
  },
  i18n,
  APPROVAL_TOOL_NAMES
};

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('gl-card',{staticClass:"gl-w-full",class:{ 'message-tool-approval-collapsed': _vm.collapsed },attrs:{"header-class":{ 'message-tool-approval-collapsed-header': _vm.collapsed },"body-class":{ 'gl-hidden': _vm.collapsed }},scopedSlots:_vm._u([{key:"header",fn:function(){return [_c('div',{staticClass:"gl-flex gl-items-center gl-justify-between gl-gap-3"},[_c('span',[(_vm.collapsible)?_c('gl-button',_vm._b({attrs:{"variant":"default","category":"tertiary","size":"small","data-testid":"toggle-details-button"},on:{"click":function($event){_vm.collapsed = !_vm.collapsed;}}},'gl-button',_vm.collapseButtonProps,false)):_vm._e(),_vm._v(" "),_c('span',[_vm._v("\n          "+_vm._s(_vm.toolApprovalTitle)+"\n        ")])],1),_vm._v(" "),_c('gl-badge',{attrs:{"variant":_vm.toolStatusVariant}},[_vm._v("\n        "+_vm._s(_vm.toolStatusLabel)+"\n      ")])],1)]},proxy:true},(!_vm.isApproved)?{key:"footer",fn:function(){return [(!_vm.showDenialReason)?_c('div',{staticClass:"gl-flex gl-gap-2"},[(_vm.showSplitButton)?_c('gl-dropdown',{attrs:{"variant":"confirm","size":"small","split":"","text":_vm.primaryApprovalOption.text,"disabled":_vm.buttonsDisabled || _vm.primaryApprovalOption.disabled,"loading":_vm.isApproving,"data-testid":"approve-dropdown"},on:{"click":_vm.handlePrimaryApprove}},_vm._l((_vm.additionalApprovalOptions),function(option){return _c('gl-dropdown-item',{key:option.type,attrs:{"disabled":option.disabled,"data-testid":"approve-dropdown-item"},on:{"click":function($event){return _vm.handleDropdownSelection(option)}}},[_vm._v("\n          "+_vm._s(option.text)+"\n        ")])}),1):_c('gl-button',{attrs:{"variant":"confirm","size":"small","data-testid":"approve-tool-inline","disabled":_vm.buttonsDisabled || _vm.primaryApprovalOption.disabled,"loading":_vm.isApproving},on:{"click":_vm.handlePrimaryApprove}},[_vm._v("\n        "+_vm._s(_vm.primaryApprovalOption.text)+"\n      ")]),_vm._v(" "),_c('gl-button',{attrs:{"size":"small","data-testid":"deny-tool-inline","disabled":_vm.buttonsDisabled,"loading":_vm.isDenying},on:{"click":_vm.handleDeny}},[_vm._v("\n        "+_vm._s(_vm.denyButtonText)+"\n      ")])],1):_c('div',[_c('gl-form-group',{staticClass:"gl-mb-3",attrs:{"label":_vm.$options.i18n.DENIAL_REASON_LABEL,"label-for":"inline-rejection-reason","optional":true}},[_c('gl-form-textarea',{attrs:{"id":"inline-rejection-reason","placeholder":_vm.$options.i18n.DENIAL_REASON_PLACEHOLDER,"rows":2,"no-resize":true,"submit-on-enter":false,"disabled":_vm.buttonsDisabled,"data-testid":"denial-reason-textarea","autofocus":""},on:{"submit":_vm.submitDenial},model:{value:(_vm.denialReason),callback:function ($$v) {_vm.denialReason=$$v;},expression:"denialReason"}})],1),_vm._v(" "),_c('div',{staticClass:"gl-flex gl-gap-3"},[_c('gl-button',{attrs:{"size":"small","data-testid":"submit-denial","variant":"confirm","disabled":_vm.buttonsDisabled,"loading":_vm.isDenying},on:{"click":_vm.submitDenial}},[_vm._v("\n          "+_vm._s(_vm.denyButtonText)+"\n        ")]),_vm._v(" "),_c('gl-button',{attrs:{"size":"small","data-testid":"cancel-denial","disabled":_vm.buttonsDisabled},on:{"click":_vm.cancelDenial}},[_vm._v("\n          "+_vm._s(_vm.$options.i18n.CANCEL_TEXT)+"\n        ")])],1)],1)]},proxy:true}:null],null,true)},[_vm._v(" "),_vm._l((_vm.messages),function(toolMsg,index){return _c('div',{key:toolMsg.id || index},[(_vm.getToolParamsComponent(toolMsg.tool_info && toolMsg.tool_info.name))?_c(_vm.getToolParamsComponent(toolMsg.tool_info && toolMsg.tool_info.name),_vm._b({tag:"component",class:['gl-leading-20', { 'gl-border-t gl-mt-3 gl-border-gray-100 gl-pt-3': index > 0 }],attrs:{"tool-name":toolMsg.tool_info && toolMsg.tool_info.name,"tool-params":_vm.convertKeysToCamelCase((toolMsg.tool_info && toolMsg.tool_info.args) || {}),"tool-response":toolMsg.tool_info && toolMsg.tool_info.tool_response,"working-directory":_vm.workingDirectory}},'component',_vm.convertKeysToCamelCase((toolMsg.tool_info && toolMsg.tool_info.args) || {}),false)):_c('figure',{staticClass:"gl-m-0 gl-flex gl-flex-col gl-gap-2",class:{ 'gl-border-t gl-mt-3 gl-border-gray-100 gl-pt-3': index > 0 }},[_c('figcaption',{staticClass:"gl-text-subtle"},[_vm._v("\n        "+_vm._s(_vm.$options.i18n.REQUEST_TEXT)+"\n      ")]),_vm._v(" "),(
          toolMsg.tool_info &&
          toolMsg.tool_info.args &&
          Object.keys(toolMsg.tool_info.args).length > 0
        )?_c('pre-block',{attrs:{"data-testid":"tool-parameters"}},[_vm._v(_vm._s(JSON.stringify(toolMsg.tool_info.args, null, 2)))]):_c('span',{staticClass:"gl-text-sm gl-text-gray-500",attrs:{"data-testid":"no-parameters-message"}},[_vm._v("\n        "+_vm._s(_vm.$options.i18n.NO_PARAMETERS_TEXT)+"\n      ")])],1)],1)})],2)};
var __vue_staticRenderFns__ = [];

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = __vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

export default __vue_component__;
export { PROCESSING_STATE, TOOL_STATUS, i18n };
